import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Cloud, Database, Server, Container, HardDrive, Zap, Shield, Activity } from "lucide-react";
import EditorialSteps from "@/components/EditorialSteps";
import TechnologyStack from "@/components/TechnologyStack";
import ServicesGrid from "@/components/ServicesGrid";
import {
  sectionVariant,
  heroVariant,
  staggerContainer,
  itemVariant,
  viewportOnce,
  glowVariant,
} from "@/lib/motion";

const services = [
  {
    name: "Cloud Storage",
    icon: Cloud,
    components: [
      "Object storage configuration",
      "Data lifecycle management",
      "Multi-region redundancy",
    ],
    outcome: "Reliable, cost-effective storage that scales with your needs."
  },
  {
    name: "BigQuery",
    icon: Database,
    components: [
      "Data warehouse architecture",
      "Query optimization",
      "Real-time analytics setup",
    ],
    outcome: "Lightning-fast analytics on petabytes of data."
  },
  {
    name: "App Engine",
    icon: Server,
    components: [
      "Application deployment",
      "Auto-scaling configuration",
      "Version management",
    ],
    outcome: "Focus on code while GCP handles infrastructure."
  },
  {
    name: "Kubernetes Engine",
    icon: Container,
    components: [
      "Cluster setup & management",
      "Workload deployment",
      "Security hardening",
    ],
    outcome: "Production-ready container orchestration."
  },
  {
    name: "Cloud SQL",
    icon: HardDrive,
    components: [
      "Database provisioning",
      "High availability setup",
      "Performance tuning",
    ],
    outcome: "Reliable databases without operational overhead."
  },
  {
    name: "Cloud Functions",
    icon: Zap,
    components: [
      "Function development",
      "Event trigger configuration",
      "API integration",
    ],
    outcome: "Code that runs only when needed, scales automatically."
  }
];

const processSteps = [
  {
    number: "01",
    title: "Assessment",
    description: "Evaluate your current infrastructure, identify cloud opportunities, and define migration strategy.",
  },
  {
    number: "02",
    title: "Architecture Design",
    description: "Design a cloud architecture optimized for performance, security, and cost efficiency.",
  },
  {
    number: "03",
    title: "Migration & Build",
    description: "Execute migration or greenfield deployment with minimal disruption to operations.",
  },
  {
    number: "04",
    title: "Optimization",
    description: "Fine-tune performance, security, and cost based on real-world usage patterns.",
  },
  {
    number: "05",
    title: "Ongoing Support",
    description: "Continuous monitoring, maintenance, and evolution of your cloud infrastructure.",
  },
];

const benefits = [
  { title: "99.99% Uptime", description: "Enterprise reliability" },
  { title: "Global Network", description: "35+ regions worldwide" },
  { title: "Cost Efficiency", description: "Pay only for what you use" },
  { title: "Security First", description: "Enterprise-grade protection" },
];

const GoogleCloudPlatform = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO SECTION */}
      <section className="relative pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full"
            initial="hidden"
            animate="visible"
            variants={glowVariant}
            style={{
              background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)',
            }}
          />
        </div>

        <div className="container-wide relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span 
                variants={heroVariant}
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block"
              >
                Google Cloud Platform
              </motion.span>
              <motion.h1 
                variants={heroVariant}
                className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]"
              >
                Cloud infrastructure that scales.
              </motion.h1>
              <motion.p 
                variants={heroVariant}
                className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl"
              >
                Expert Google Cloud services from architecture to implementation. 
                We build, migrate, and optimize cloud infrastructure for growth.
              </motion.p>
              <motion.div 
                variants={heroVariant}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button size="lg" className="group" asChild>
                  <Link to="/contact">
                    Discuss your cloud needs
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-8 border-t border-border bg-muted/30">
        <div className="container-wide">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {benefits.map((benefit) => (
              <motion.div key={benefit.title} variants={itemVariant} className="text-center">
                <div className="text-sm font-medium text-foreground mb-1">{benefit.title}</div>
                <div className="text-xs text-muted-foreground">{benefit.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-10 md:py-16 lg:py-20 border-t border-border">
        <div className="container-wide">
          <motion.div 
            className="max-w-3xl"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <motion.span 
              variants={sectionVariant}
              className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block"
            >
              The Problem
            </motion.span>
            <motion.h2 
              variants={sectionVariant}
              className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-10"
            >
              Why cloud migrations stall.
            </motion.h2>
            
            <motion.div className="space-y-6" variants={staggerContainer}>
              {[
                "Lift-and-shift migrations move problems without solving them.",
                "Poor architecture decisions create unexpected cost overruns.",
                "Misconfigured services introduce security vulnerabilities.",
                "Resources are over-provisioned, inflating operational costs.",
              ].map((problem, index) => (
                <motion.div 
                  key={index} 
                  variants={itemVariant} 
                  className={`border-l-2 ${index === 0 ? "border-primary" : "border-border"} pl-6`}
                >
                  <p className={index === 0 ? "text-foreground font-medium" : "text-muted-foreground"}>
                    {problem}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES - Using ServicesGrid */}
      <ServicesGrid
        label="The System"
        title="Full-stack Google Cloud expertise."
        services={services}
      />

      {/* PROCESS - Editorial Steps */}
      <EditorialSteps 
        title="From assessment to cloud-native."
        steps={processSteps}
      />

      {/* Technology Stack */}
      <TechnologyStack
        label="GCP Services"
        title="Full Google Cloud expertise."
        description="Certified expertise across the complete Google Cloud Platform ecosystem."
        categories={[
          {
            title: "Compute",
            icon: Server,
            items: ["Compute Engine", "App Engine", "Cloud Run", "GKE"]
          },
          {
            title: "Data & Analytics",
            icon: Database,
            items: ["BigQuery", "Cloud SQL", "Firestore", "Bigtable"]
          },
          {
            title: "Security",
            icon: Shield,
            items: ["IAM", "Cloud Armor", "Secret Manager", "VPC"]
          },
          {
            title: "Monitoring",
            icon: Activity,
            items: ["Cloud Monitoring", "Cloud Logging", "Error Reporting", "Trace"]
          }
        ]}
      />

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-border bg-muted/20">
        <div className="container-wide">
          <motion.div 
            className="max-w-2xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
          >
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Ready to move to the cloud?
            </h2>
            <p className="text-muted-foreground mb-8">
              Let's discuss how Google Cloud can power your infrastructure.
            </p>
            <Button size="lg" className="group" asChild>
              <Link to="/contact">
                Start the conversation
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GoogleCloudPlatform;